import styled, { keyframes } from "styled-components";
import Card from "components/UI/Card";
import { modeStyle } from "utils/styles";

export type PromptPosition = "top" | "bottom";
export type PromptSpacing = "tight" | "wide" | "";

const fadein = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const Overlay = styled.div<{ position: PromptPosition }>`
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 9999;
  padding: 24px;
  display: flex;
  animation: ${fadein} 300ms ease forwards;

  ${(props) =>
    props.position === "top" &&
    `flex-direction: column;
    align-items: flex-end;
    `}

  ${(props) =>
    props.position === "bottom" &&
    `flex-direction: column-reverse;
    align-items: center;
    `}
`;

export const MobilePrompt = styled(Card)<{
  position: PromptPosition;
  spacing: PromptSpacing;
}>`
  box-sizing: border-box;
  width: 100%;
  position: relative;
  padding: 16px;
  max-width: ${(props) => (props.spacing === "tight" ? "300px" : "400px")};
  box-shadow: 1px 5px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${modeStyle("black", "white")};
  animation: ${fadein} 300ms ease forwards;

  &::before {
    content: "";
    position: absolute;
    ${(props) =>
      props.position === "top" &&
      `
      bottom: calc(100% - 14px);
      right: ${props.spacing === "wide" ? "108px" : "94px"};
    `}

    ${(props) =>
      props.position === "bottom" &&
      `
    top: calc(100% - 13px);
    left: calc(50% - 14px);
    `}
    height: 28px;
    width: 28px;
    background: ${modeStyle("white", "#353535")};
    border-bottom: 1.5px solid ${modeStyle("#dcdcdc", "#5D5D5D")};
    border-right: 1.5px solid ${modeStyle("#dcdcdc", "#5D5D5D")};
    box-sizing: border-box;
    transform: scaleX(0.5) rotate(45deg);
    z-index: -1;
  }

  /* same as above, but without border and z-index to cover container border */
  &::after {
    content: "";
    position: absolute;
    ${(props) =>
      props.position === "top" &&
      `
      bottom: calc(100% - 14px);
      right: ${props.spacing === "wide" ? "109px" : "95px"};
    `}

    ${(props) =>
      props.position === "bottom" &&
      `
      top: calc(100% - 14px);
      left: calc(50% - 13px);
    `}
    height: 26px;
    width: 26px;
    background: ${modeStyle("white", "#353535")};
    box-sizing: border-box;
    transform: scaleX(0.5) rotate(45deg);
  }
`;

export const DismissPromptButton = styled.button`
  background: none;
  border: none;
  outline: none;
  position: absolute;
  width: 12px;
  height: 12px;
  padding: 10px;
  top: 0px;
  right: 0px;
  box-sizing: content-box;
  color: #9e9e9e;
`;

export const Header = styled.div`
  text-align: center;
  width: 100%;
  padding: 12px 0 8px;
  margin-bottom: 16px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    width: calc(100% - 48px);
    height: 1px;
    background: #e6e6e8;
    transform: translateX(-50%);
  }
`;

export const Footer = styled.div`
  text-align: center;
  border-top: 1px solid #dcdcdc;
  margin-top: 24px;
  padding-top: 12px;
  width: calc(100% + 32px);
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const popin = keyframes`
  from { transform: scale(0); }
  to { transform: scale(1); }
`;

const popout = keyframes`
  from { transform: scale(1); opacity: 1; }
  to { transform: scale(0); opacity: 0;}
`;

const dropin = keyframes`
  from { transform: scale(1.25); opacity: 0%; }
  to { transform: scale(1); opacity: 100%; }
`;

const shake = keyframes`
  0% { transform: scale(1); }
  10% { transform: scale(1.4) rotate(0deg); }
  18% { transform: scale(1.4) rotate(-25deg); }
  34% { transform: scale(1.4) rotate(25deg); }
  50% { transform: scale(1.4) rotate(-25deg); }
  66% { transform: scale(1.4) rotate(25deg); }
  82% { transform: scale(1.4) rotate(-25deg); }
  90% { transform: scale(1.4) rotate(0deg); }
  100% { transform: scale(1); }
`;

const handClick = keyframes`
  0% { transform: translate(0) scale(1); }
  40% { transform: translate(-5%, -15%) scale(.9); }
  100% { transform: translate(0) scale(1); }
`;

const headTilt = keyframes`
 0% { transform: rotate(0); }
 100% { transform: rotate(10deg); }
`;

export const AnimatedSvg = styled.svg<{ spacing: PromptSpacing }>`
  width: ${(props) => (props.spacing === "tight" ? "75%" : "100%")};

  #outter-ring,
  #inner-ring,
  #share-icon,
  #add-icon,
  #add-icon-plus,
  .app-icon,
  .smiley {
    transform-origin: center;
  }

  #outter-ring {
    animation: ${rotate} 8s linear infinite;
  }

  #inner-ring {
    animation: ${rotate} 8s linear infinite;
    animation-direction: reverse;
  }

  #share-icon {
    transform: scale(0);
    animation: ${popin} 200ms cubic-bezier(0.25, 1, 0.5, 1) 500ms forwards,
      ${popout} 75ms linear 2300ms forwards;
  }

  #add-icon,
  #add-icon-plus {
    transform: scale(0);
    animation: ${popin} 200ms cubic-bezier(0.25, 1, 0.5, 1) 2400ms forwards,
      ${popout} 75ms linear 4100ms forwards;
  }

  .app-icon {
    transform: scale(0);
    animation: ${dropin} 450ms ease 4200ms forwards;
  }

  .red-app-icon {
    animation: ${dropin} 450ms ease 4200ms forwards,
      ${shake} 1200ms cubic-bezier(0.45, 0, 0.55, 1) 4700ms forwards;
    transform-origin: calc(50% + 12px) calc(50% + 12px);
  }

  .hand {
    transform: scale(0);
    animation: ${popin} 300ms cubic-bezier(0.25, 1, 0.5, 1) 1000ms forwards,
      ${handClick} 1000ms linear 1700ms forwards,
      ${handClick} 1000ms linear 3500ms forwards,
      ${popout} 300ms cubic-bezier(0.25, 1, 0.5, 1) 4700ms forwards;
    transform-origin: 75% 100%;
  }

  .smiley {
    animation: ${popin} 300ms cubic-bezier(0.25, 1, 0.5, 1) 6000ms forwards,
      ${headTilt} 300ms cubic-bezier(0.25, 1, 0.5, 1) 6300ms forwards;
    transform-origin: 75% 100%;
  }

  .hand,
  .app-icon,
  .smiley {
    transform: scale(0);
  }
`;
